import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Typography, Box, Button, Grid, Divider, CircularProgress,
} from '@material-ui/core';
import ExtensionIcon from '@material-ui/icons/Extension';
import SaveIcon from '@material-ui/icons/Save';
import BlockIcon from '@material-ui/icons/Block';
import axios from 'axios';
import SellerMultiSelect from './shared/SellerMultiSelect';
import OptionMultiSelect from './shared/OptionMultiSelect';
import { useProductFilterMaster } from './shared/productFilterApiHelpers';
import InfoBanner from './shared/InfoBanner';
import SnackbarAlert from './shared/SnackbarAlert';
import useSnackbar from './shared/useSnackbar';
import {
  getApiBase, assertOkEnvelope, nonDynamicUrlHeaders, slaHeaders, SLA_PATH_PATTERN,
} from './shared/slaApiHelpers';

const FD_EXCLUSIONS = SLA_PATH_PATTERN.ADDRESS_FAST_DELIVERY_EXCLUSIONS;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: 'calc(100vh - 64px)',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  titleIcon: {
    color: '#3f51b5',
    fontSize: 32,
    marginRight: theme.spacing(1),
  },
  title: {
    fontSize: '28px',
    fontWeight: 400,
    color: '#333',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
  },
  card: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  sectionIcon: {
    color: '#3f51b5',
    fontSize: 24,
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 400,
    color: '#333',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: theme.spacing(3),
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  saveButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    textTransform: 'none',
    padding: theme.spacing(1.5, 4),
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': { backgroundColor: '#303f9f' },
    '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
  },
  loadingOverlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
}));

const Additional = () => {
  const classes = useStyles();
  const { snackbar, showSnack, closeSnack } = useSnackbar();
  const [soldBy, setSoldBy] = useState([]);
  const [sellerIds, setSellerIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { soldBy: soldByOptions, loading: filterLoading, error: filterError } = useProductFilterMaster();

  const fetchExclusions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${getApiBase()}${FD_EXCLUSIONS}`, {
        headers: slaHeaders(nonDynamicUrlHeaders(FD_EXCLUSIONS)),
      });
      assertOkEnvelope(res);
      const data = res.data?.data || {};
      setSoldBy(Array.isArray(data.sold_by) ? data.sold_by : []);
      setSellerIds(Array.isArray(data.seller_ids) ? data.seller_ids : []);
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to load exclusion configuration.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnack]);

  useEffect(() => { fetchExclusions(); }, [fetchExclusions]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `${getApiBase()}${FD_EXCLUSIONS}`,
        { sold_by: soldBy, seller_ids: sellerIds },
        {
          headers: slaHeaders(nonDynamicUrlHeaders(FD_EXCLUSIONS)),
        }
      );
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || 'Fast delivery exclusion configuration saved successfully!');
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to save configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleContainer}>
          <ExtensionIcon className={classes.titleIcon} />
          <Typography className={classes.title}>Fast Delivery Exclusions</Typography>
        </Box>
        <Typography className={classes.subtitle}>
          Configure SKU attribute exclusions for fast delivery (same-day / next-day) eligibility
        </Typography>
      </Box>

      <InfoBanner>
        Fast delivery (same-day or next-day) should not apply to SKUs that have a first-mile or mid-mile
        SLA and are not sourced from a Styli last-mile warehouse. Use the configuration below to exclude
        specific <strong>Sold By</strong> values and <strong>Seller IDs</strong> from fast delivery.
        SKUs matching these exclusions will fall back to normal SLA calculation
        (first-mile + mid-mile + last-mile). Shipment-level SLA is determined by the longest SLA in
        the order &mdash; any excluded SKU in an order will prevent the entire shipment from being
        tagged as fast delivery.
      </InfoBanner>

      {loading ? (
        <Box className={classes.loadingOverlay}><CircularProgress /></Box>
      ) : (
        <Paper className={classes.card}>
          <Box className={classes.sectionHeader}>
            <BlockIcon className={classes.sectionIcon} />
            <Typography className={classes.sectionTitle}>Exclusion configuration</Typography>
          </Box>
          <Typography className={classes.sectionSubtitle}>
            SKUs matching any of the values below will be excluded from fast delivery calculation.
            The express flag to Increff will only be sent when all SKUs in the order fulfill fast delivery criteria.
          </Typography>
          <Divider style={{ marginBottom: 24 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <OptionMultiSelect
                label="Sold By"
                placeholder="Search and select sold-by values"
                options={soldByOptions}
                values={soldBy}
                onChange={setSoldBy}
                loading={filterLoading}
                error={filterError}
                emptyText="No sold-by values found"
                loadingText="Loading sold-by values..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SellerMultiSelect
                label="Sellers"
                placeholder="Search and select sellers"
                values={sellerIds}
                onChange={setSellerIds}
              />
            </Grid>
          </Grid>

          <Box className={classes.buttonRow}>
            <Button
              variant="contained"
              className={classes.saveButton}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save configuration'}
            </Button>
          </Box>
        </Paper>
      )}

      <SnackbarAlert open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={closeSnack} />
    </div>
  );
};

export default Additional;
